import { getUserImageUrl } from '../constants/config';
import { champFields } from './tournamentsApi';
import { normalizeRoleIds, userDisplayName } from './usersApi';
import { resolveMatchTimezone, utcKickoffToWall, parseUtcKickoffFields } from './matchKickoff';

export const EMPTY_REFEREE_ID = 2;

export const ROLE_REFEREE = 13;

export const REFEREE_SLOT_OPTIONS = [
  { slot: 'judje_id', labelKey: 'msRefereeMain' },
  { slot: 'second_judje_id', labelKey: 'msRefereeAssistant1' },
  { slot: 'third_judje_id', labelKey: 'msRefereeAssistant2' },
  { slot: 'fourth_judje_id', labelKey: 'msRefereeTimekeeper' },
  { slot: 'fifth_judje_id', labelKey: 'msRefereeLine' },
];

export const refereeSlotLabelKey = (slot) => (
  REFEREE_SLOT_OPTIONS.find((item) => item.slot === slot)?.labelKey || null
);

export const isRefereeRole = (roleId) => Number(roleId) === ROLE_REFEREE;

export const ROLE_SLOT_GROUPS = [
  { role: 13, slots: ['judje_id', 'second_judje_id', 'third_judje_id', 'fourth_judje_id', 'fifth_judje_id'] },
  { role: 8, slots: ['cameraman_id', 'second_cameraman_id'] },
  { role: 10, slots: ['photographer_id', 'second_photographer_id'] },
  { role: 11, slots: ['medic_id'] },
  { role: 9, slots: ['comment_id'] },
  { role: 5, slots: ['administrator_id'] },
  { role: 12, slots: [] },
];

export const ALL_STAFF_SLOTS = ROLE_SLOT_GROUPS.flatMap((group) => group.slots);

const SLOT_TO_ROLE = ALL_STAFF_SLOTS.reduce((acc, slot) => {
  const group = ROLE_SLOT_GROUPS.find((item) => item.slots.includes(slot));
  if (group) acc[slot] = group.role;
  return acc;
}, {});

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const unwrapPayload = (data) => {
  if (data && typeof data === 'object' && data.data && typeof data.data === 'object' && !Array.isArray(data.data)) {
    if (data.staff || data.matches) return data;
    return data.data;
  }
  return data && typeof data === 'object' ? data : {};
};

export const staffPersonId = (person) => toNumber(person?.id ?? person?.user_id);

export const staffRowKey = (person) => {
  const id = staffPersonId(person);
  const role = toNumber(person?.role);
  if (!id) return '';
  return role ? `${id}:${role}` : String(id);
};

export const isEmptyRefereeId = (value) => {
  const id = toNumber(value);
  return id == null || id <= 0 || id === EMPTY_REFEREE_ID;
};

export const isEmptySlotValue = (slot, value) => {
  if (slot === 'judje_id') return isEmptyRefereeId(value);
  const id = toNumber(value);
  return id == null || id <= 0;
};

export const staffDisplayName = (person) => (
  userDisplayName(person) || person?.login || `#${staffPersonId(person) || ''}`
);

const staffSortField = (value) => String(value || '').trim().toLocaleLowerCase('ru');

export const compareStaffPeople = (left, right) => {
  const family = staffSortField(left?.family).localeCompare(staffSortField(right?.family), 'ru');
  if (family) return family;
  const name = staffSortField(left?.name).localeCompare(staffSortField(right?.name), 'ru');
  if (name) return name;
  const login = staffSortField(left?.login).localeCompare(staffSortField(right?.login), 'ru');
  if (login) return login;
  return (toNumber(left?.role) || 0) - (toNumber(right?.role) || 0);
};

const ROLE_SORT_ORDER = ROLE_SLOT_GROUPS.map((item) => item.role);

export const compareStaffByRoleThenName = (left, right) => {
  const leftRole = ROLE_SORT_ORDER.indexOf(toNumber(left?.role));
  const rightRole = ROLE_SORT_ORDER.indexOf(toNumber(right?.role));
  const leftIdx = leftRole < 0 ? 99 : leftRole;
  const rightIdx = rightRole < 0 ? 99 : rightRole;
  if (leftIdx !== rightIdx) return leftIdx - rightIdx;
  return compareStaffPeople(left, right);
};

export const staffPhotoUrl = (person) => getUserImageUrl(person?.image || person?.photo || person?.avatar);

export const staffRoleIds = (person) => {
  const role = toNumber(person?.role);
  if (role) return [role];
  return normalizeRoleIds(person?.roles);
};

export const assignableRoles = (person) => (
  staffRoleIds(person).filter((role) => {
    const group = ROLE_SLOT_GROUPS.find((item) => item.role === role);
    return group && group.slots.length > 0;
  })
);

export const roleSlotGroup = (roleId) => (
  ROLE_SLOT_GROUPS.find((item) => item.role === Number(roleId)) || null
);

export const normalizeStaffPerson = (item = {}) => {
  const id = staffPersonId(item);
  const role = toNumber(item.role);
  const position = item.position || '';
  return {
    ...item,
    id,
    user_id: id,
    role,
    roles: role ? [role] : normalizeRoleIds(item.roles),
    position,
    positions: position ? [position] : [],
    games: toNumber(item.games) || 0,
    image: item.image || item.photo || item.avatar || '',
    rowKey: staffRowKey({ id, role }),
  };
};

export const teamFrom = (raw, fallbackName, fallbackImage, fallbackShort) => {
  if (raw && typeof raw === 'object') {
    return {
      id: toNumber(raw.id),
      name: raw.name || raw.team_name || fallbackName || '',
      short_name: raw.short_name ?? raw.shortName ?? fallbackShort ?? null,
      image: raw.image || raw.logo || fallbackImage || '',
    };
  }
  return {
    id: null,
    name: fallbackName || '',
    short_name: fallbackShort ?? null,
    image: fallbackImage || '',
  };
};

export const teamCardName = (team) => team?.short_name || team?.name || '—';

const stadiumFrom = (raw, fallbackName) => {
  if (raw && typeof raw === 'object') {
    const id = toNumber(raw.id);
    const name = raw.name || raw.stadium_name || fallbackName || '';
    return {
      id,
      name,
      address: raw.address || '',
      image: raw.image || '',
      key: id ? `s-${id}` : `s-${name || 'none'}`,
    };
  }
  const name = fallbackName || '';
  return name
    ? { id: null, name, address: '', image: '', key: `s-${name}` }
    : { id: null, name: '', address: '', image: '', key: 's-none' };
};

/** Поле стадиона: как у карточки матча — { id, name, image } | null. */
const pitchFrom = (raw, fallbackId, fallbackName) => {
  const fromObj = (obj, nameFallback = '') => {
    if (!obj || typeof obj !== 'object') return null;
    const id = toNumber(obj.id ?? obj.pitch_id ?? obj.field_id);
    const name = obj.name || obj.pitch_name || obj.field_name || nameFallback || '';
    if (!id && !name) return null;
    return {
      id: id && id > 0 ? id : null,
      name,
      image: obj.image || '',
      key: id && id > 0 ? `p-${id}` : `p-${name}`,
    };
  };
  const nested = fromObj(raw, fallbackName);
  if (nested) return nested;
  const id = toNumber(fallbackId ?? raw);
  const name = fallbackName || '';
  if (!id && !name) return null;
  return {
    id: id && id > 0 ? id : null,
    name,
    image: '',
    key: id && id > 0 ? `p-${id}` : `p-${name || 'none'}`,
  };
};

const slotValue = (match, slot) => {
  const nested = match?.slots && typeof match.slots === 'object' ? match.slots[slot] : undefined;
  const raw = match?.[slot] ?? nested;
  if (raw && typeof raw === 'object') return toNumber(raw.id ?? raw.user_id);
  return toNumber(raw);
};

const catalogPersonFor = (staff = [], userId, role) => {
  const id = toNumber(userId);
  if (!id) return null;
  const roleId = toNumber(role);
  if (roleId) {
    const match = staff.find((item) => staffPersonId(item) === id && toNumber(item.role) === roleId);
    if (match) return match;
  }
  return staff.find((item) => staffPersonId(item) === id) || null;
};

export const matchStaffList = (match, catalog = []) => {
  const fromApi = Array.isArray(match?.staff) ? match.staff : [];
  const usedSlots = new Set();
  const staff = [];

  const pushPerson = (person, slot, roleHint) => {
    const id = staffPersonId(person);
    if (!id || usedSlots.has(slot)) return;
    const role = toNumber(roleHint) || SLOT_TO_ROLE[slot] || toNumber(person.role);
    const catalogPerson = catalogPersonFor(catalog, id, role) || {};
    usedSlots.add(slot);
    staff.push({
      ...normalizeStaffPerson({ ...catalogPerson, ...person, role }),
      slot,
      role,
      position: person.position || catalogPerson.position || '',
    });
  };

  fromApi.forEach((item) => {
    const slot = item?.slot;
    if (!slot || !ALL_STAFF_SLOTS.includes(slot)) return;
    if (isEmptySlotValue(slot, item.id ?? item.user_id)) return;
    pushPerson(item, slot, item.role);
  });

  ALL_STAFF_SLOTS.forEach((slot) => {
    if (usedSlots.has(slot)) return;
    const id = slotValue(match, slot);
    if (isEmptySlotValue(slot, id)) return;
    const role = SLOT_TO_ROLE[slot];
    const person = catalogPersonFor(catalog, id, role)
      || fromApi.find((item) => staffPersonId(item) === id && toNumber(item.role) === role)
      || fromApi.find((item) => staffPersonId(item) === id)
      || { id, role };
    pushPerson(person, slot, role);
  });

  return staff.sort((a, b) => ALL_STAFF_SLOTS.indexOf(a.slot) - ALL_STAFF_SLOTS.indexOf(b.slot));
};

/**
 * @param {object} item сырой матч из API (или уже нормализованный с date_utc)
 * @param {array} catalog каталог сотрудников
 * @param {object|null} venueContext чемпионат/лига/город (как на стадии и в карточке матча)
 */
export const normalizeStaffMatch = (item = {}, catalog = [], venueContext = null) => {
  const staff = matchStaffList(item, catalog);
  const slots = {};
  ALL_STAFF_SLOTS.forEach((slot) => {
    const person = staff.find((entry) => entry.slot === slot);
    slots[slot] = person ? person.id : null;
  });
  const settings = item.settings && typeof item.settings === 'object' ? item.settings : {};
  const league = item.league && typeof item.league === 'object'
    ? item.league
    : { id: toNumber(item.league_id), name: item.league_name || '' };
  const tournament = item.tournament && typeof item.tournament === 'object'
    ? item.tournament
    : { id: toNumber(item.champ_id || item.championat_id), name: item.tournament_name || '' };
  // Тот же порядок источников, что в normalizeAdminMatch / formatKickoff
  const timezone = resolveMatchTimezone(
    item.venue_timezone,
    item,
    settings,
    item.stadium,
    item.league,
    league,
    item.championship,
    tournament,
    venueContext,
    {
      city: item.city_name || item.city,
      city_ru: item.city_ru,
      city_id: item.city_id,
      timezone: item.city?.timezone,
      name: item.country_name || item.country?.name || league.country_name,
    },
  );
  const parsed = parseUtcKickoffFields(item);
  const rawDate = parsed.date;
  const rawTime = parsed.time;
  const wall = rawDate && rawTime ? utcKickoffToWall(rawDate, rawTime, timezone) : null;
  return {
    ...item,
    id: toNumber(item.id ?? item.match_id),
    date: wall?.date || rawDate,
    time: wall?.time || rawTime,
    date_utc: rawDate,
    time_utc: rawTime,
    timezone,
    venue_timezone: item.venue_timezone || timezone,
    league_id: toNumber(item.league_id),
    league_name: item.league_name || league.name || '',
    home_team: teamFrom(
      item.home_team,
      item.home_team_name,
      item.home_team_image,
      item.home_team_short_name
    ),
    guest_team: teamFrom(
      item.guest_team,
      item.guest_team_name,
      item.guest_team_image,
      item.guest_team_short_name
    ),
    stadium: stadiumFrom(item.stadium, item.stadium_name),
    pitch: pitchFrom(
      item.pitch || item.field,
      item.pitch_id ?? item.field_id,
      item.pitch_name || item.field_name
    ),
    staff,
    ...slots,
  };
};

export const extractAdminStaff = (data) => {
  const payload = unwrapPayload(data);
  const list = Array.isArray(payload.staff)
    ? payload.staff
    : (Array.isArray(payload) ? payload : []);
  return list.map(normalizeStaffPerson).filter((item) => item.id).sort(compareStaffPeople);
};

export const extractAdminStaffMatches = (data, catalog = [], venueContext = null) => {
  const payload = unwrapPayload(data);
  const list = Array.isArray(payload.matches) ? payload.matches : [];
  return {
    from: payload.from || '',
    to: payload.to || '',
    matches: list
      .map((item) => normalizeStaffMatch(item, catalog, venueContext))
      .filter((item) => item.id),
  };
};

const occupiedSlotsForRole = (match, roleId) => {
  const group = roleSlotGroup(roleId);
  if (!group) return [];
  return (match?.staff || []).filter((item) => group.slots.includes(item.slot));
};

export const matchHasPersonRole = (match, personId, roleId) => {
  const id = Number(personId);
  const role = toNumber(roleId);
  if (!id || !role) return false;
  const group = roleSlotGroup(role);
  const slots = new Set(group?.slots || []);
  return (match?.staff || []).some((item) => (
    staffPersonId(item) === id && (toNumber(item.role) === role || slots.has(item.slot))
  ));
};

export const freeSlotsForRole = (match, roleId) => {
  const group = roleSlotGroup(roleId);
  if (!group) return [];
  const taken = new Set(occupiedSlotsForRole(match, roleId).map((item) => item.slot));
  return group.slots.filter((slot) => !taken.has(slot));
};

export const assignmentTarget = (match, person, preferredSlot) => {
  const id = staffPersonId(person);
  if (!id) return { ok: false, reason: 'missing' };

  const role = toNumber(person.role);
  const group = roleSlotGroup(role);
  if (!group || !group.slots.length) return { ok: false, reason: 'no-slot' };
  if (matchHasPersonRole(match, id, role)) return { ok: false, reason: 'already' };

  const free = freeSlotsForRole(match, role);
  if (!free.length) return { ok: false, reason: 'full' };
  if (preferredSlot) {
    if (!group.slots.includes(preferredSlot)) return { ok: false, reason: 'no-slot' };
    if (!free.includes(preferredSlot)) return { ok: false, reason: 'full' };
    return { ok: true, slot: preferredSlot, role, slots: free };
  }
  return { ok: true, slot: free[0], role, slots: free };
};

export const assignPersonToMatch = (match, person, preferredSlot) => {
  const target = assignmentTarget(match, person, preferredSlot);
  if (!target.ok) return { match, ...target };
  const nextPerson = {
    ...normalizeStaffPerson(person),
    slot: target.slot,
    role: target.role,
    position: person.position || '',
  };
  const staff = [...(match.staff || []), nextPerson]
    .sort((a, b) => ALL_STAFF_SLOTS.indexOf(a.slot) - ALL_STAFF_SLOTS.indexOf(b.slot));
  return {
    ok: true,
    reason: null,
    match: {
      ...match,
      staff,
      [target.slot]: nextPerson.id,
    },
  };
};

export const removePersonFromMatch = (match, personId, slot) => {
  const staff = (match.staff || []).filter((item) => {
    if (slot) return !(item.slot === slot && staffPersonId(item) === Number(personId));
    return staffPersonId(item) !== Number(personId);
  });
  const next = { ...match, staff };
  ALL_STAFF_SLOTS.forEach((key) => {
    const person = staff.find((item) => item.slot === key);
    next[key] = person ? person.id : null;
  });
  return next;
};

export const groupMatchesByDateAndStadium = (matches = []) => {
  const byDate = new Map();
  [...matches]
    .sort((a, b) => {
      const dateCmp = String(a.date).localeCompare(String(b.date));
      if (dateCmp) return dateCmp;
      const stadiumCmp = String(a.stadium?.name || '').localeCompare(String(b.stadium?.name || ''), 'ru');
      if (stadiumCmp) return stadiumCmp;
      const pitchCmp = String(a.pitch?.name || '').localeCompare(String(b.pitch?.name || ''), 'ru');
      if (pitchCmp) return pitchCmp;
      return String(a.time || '').localeCompare(String(b.time || ''));
    })
    .forEach((match) => {
      const dateKey = match.date || 'unknown';
      if (!byDate.has(dateKey)) byDate.set(dateKey, new Map());
      const stadiums = byDate.get(dateKey);
      const stadiumKey = match.stadium?.key || 's-none';
      if (!stadiums.has(stadiumKey)) {
        stadiums.set(stadiumKey, {
          stadium: match.stadium,
          pitches: [],
          pitchIndex: new Map(),
        });
      }
      const stadiumGroup = stadiums.get(stadiumKey);
      const pitchKey = match.pitch?.key || 'p-none';
      if (!stadiumGroup.pitchIndex.has(pitchKey)) {
        const pitchGroup = { pitch: match.pitch || null, matches: [] };
        stadiumGroup.pitchIndex.set(pitchKey, pitchGroup);
        stadiumGroup.pitches.push(pitchGroup);
      }
      stadiumGroup.pitchIndex.get(pitchKey).matches.push(match);
    });

  return [...byDate.entries()].map(([date, stadiums]) => ({
    date,
    stadiums: [...stadiums.values()].map(({ pitchIndex, ...stadiumGroup }) => stadiumGroup),
  }));
};

export const matchStaffSignature = (match) => ALL_STAFF_SLOTS
  .map((slot) => {
    const person = (match.staff || []).find((item) => item.slot === slot);
    return `${slot}:${person ? person.id : ''}`;
  })
  .join('|');

export const matchToSavePayload = (match) => {
  const staff = [];
  const payload = { id: match.id };
  ALL_STAFF_SLOTS.forEach((slot) => {
    const person = (match.staff || []).find((item) => item.slot === slot);
    payload[slot] = person
      ? person.id
      : (slot === 'judje_id' ? EMPTY_REFEREE_ID : null);
    if (person) {
      staff.push({
        user_id: person.id,
        role: person.role,
        slot,
      });
    }
  });
  if (staff.length) payload.staff = staff;
  return payload;
};

export const staffRequestBody = (champId, extra = {}) => ({
  ...champFields(champId),
  ...extra,
});
